/**
 * Text highlight for Milkdown: ProseMirror mark `highlight`, markdown `==like this==`, DOM `<mark>`.
 *
 * **Pipeline**
 * 1. **Parse (markdown → doc)** — `remarkHighlightSyntax` runs after `remark-parse` and rewrites plain
 *    `text` nodes that contain `==...==` into custom mdast nodes `{ type: 'highlight', children: [...] }`.
 *    Milkdown’s `ParserState` then maps those nodes onto `highlightSchema` via `parseMarkdown`.
 * 2. **Serialize (doc → markdown)** — `toMarkdown` on the mark emits mdast `highlight` wrappers;
 *    `highlightMarkdownHandler` (registered in `configure.ts` via `remarkStringifyOptionsCtx`) turns
 *    them into the literal string `==inner==`. `highlightMdastUnsafe` tells `mdast-util-to-markdown`
 *    that `=` is allowed inside phrasing so those delimiters are not over-escaped.
 * 3. **Editing** — `toggleHighlightCommand` (toolbar) and `highlightInputRule` (typing `==`) apply
 *    or remove the mark; paste/HTML can use `<mark>` via `parseDOM`.
 *
 * **Not in this file** — Merging `handlers.highlight` and `unsafe` into the remark stringify options
 * lives in `configure.ts` so Crepe’s editor init stays a one-liner (`configureHighlight`).
 */
import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import type { Info, State } from 'mdast-util-to-markdown';
import type { Parents, Root } from 'mdast';
import { $command, $inputRule, $markSchema, $remark } from '@milkdown/kit/utils';
import { markRule } from '@milkdown/prose';
import { toggleMark } from '@milkdown/prose/commands';
import { visitParents } from 'unist-util-visit-parents';

/**
 * ProseMirror mark registered as `highlight` in the schema.
 *
 * - **DOM** — Renders as `<mark>` so HTML paste and copy round-trip with the same tag.
 * - **Markdown** — Expects companion mdast nodes with `type: 'highlight'` (produced by the remark
 *   transform below, not by stock GFM). Serializes back through `toMarkdown` + the custom stringify
 *   handler in `configure.ts`.
 */
export const highlightSchema = $markSchema('highlight', () => ({
  parseDOM: [{ tag: 'mark' }],
  toDOM: () => ['mark', 0] as const,
  parseMarkdown: {
    match: (node: { type?: string }) => node.type === 'highlight',
    runner: (state, node, markType) => {
      state.openMark(markType);
      state.next(node.children);
      state.closeMark(markType);
    },
  },
  toMarkdown: {
    match: (mark) => mark.type.name === 'highlight',
    runner: (state, mark) => {
      state.withMark(mark, 'highlight');
    },
  },
}));

/** Command used by the formatting toolbar; toggles the highlight mark on the active selection. */
export const toggleHighlightCommand = $command('ToggleHighlight', (ctx) => () => toggleMark(highlightSchema.type(ctx)));

/**
 * When the user types closing `==` after `==some text`, wraps the inner span in the highlight mark
 * and strips the delimiters (same idea as GFM `~~` strikethrough input rules).
 *
 * Regex groups: `(==)` `([^=\n]+)` `\1` — `markRule` uses the **last** capture group as the marked
 * span, so the middle group must be the highlighted text. Lookbehind/lookahead avoid matching four
 * or more equals in a row as a single highlight.
 */
export const highlightInputRule = $inputRule((ctx) => markRule(/(?<!=)(==)([^=\n]+)\1(?!=)/, highlightSchema.type(ctx)));

/** mdast shape we synthesize in `transformHighlightSyntax` before Milkdown parses the tree. */
type HighlightMdast = { type: 'highlight'; children: { type: 'text'; value: string }[] };

/**
 * Splits a single `text` node’s `value` on non-overlapping `==segment==` patterns.
 *
 * - Content between pairs becomes `{ kind: 'highlight', value }` (inner text only, no delimiters).
 * - Outside segments become `{ kind: 'text', value }`.
 * - Does not match empty inner segments (`====` yields no highlight chunk from `[^=\n]+`).
 * - If there are no `==...==` matches, returns one plain text segment (caller may no-op).
 */
function splitHighlightInValue(value: string): (TextSegment | HighlightSegment)[] {
  const re = /==([^=\n]+)==/g;
  const out: (TextSegment | HighlightSegment)[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(value)) !== null) {
    if (m.index > last) {
      out.push({ kind: 'text', value: value.slice(last, m.index) });
    }
    out.push({ kind: 'highlight', value: m[1] });
    last = m.index + m[0].length;
  }
  if (last < value.length) {
    out.push({ kind: 'text', value: value.slice(last) });
  }

  return out.length ? out : [{ kind: 'text', value }];
}

type TextSegment = { kind: 'text'; value: string };
type HighlightSegment = { kind: 'highlight'; value: string };

/**
 * Post-parse mdast pass: turn literal `==highlight==` in text into structural `highlight` nodes
 * so `highlightSchema.parseMarkdown` can run.
 *
 * Uses `visitParents` (not plain `visit`) so we can skip fenced `code` blocks: under `code`, mdast
 * still uses `text` children, and we must not treat documentation examples of `==` as real highlights.
 * Replaces one `text` node with a flat list of `text` + `highlight` siblings in the same parent.
 */
function transformHighlightSyntax(tree: Root) {
  visitParents(tree, 'text', (node, ancestors) => {
    if (!node.value.includes('==')) return;
    if (ancestors.some((a) => (a as { type?: string }).type === 'code')) return;

    const parent = ancestors[ancestors.length - 1] as {
      children?: unknown[];
    };
    if (!parent?.children || !Array.isArray(parent.children)) return;

    const index = parent.children.indexOf(node);
    if (index === -1) return;

    const segments = splitHighlightInValue(node.value);
    if (segments.length === 1 && segments[0].kind === 'text') return;

    const replacements: (typeof node | HighlightMdast)[] = [];
    for (const seg of segments) {
      if (seg.kind === 'text') {
        if (seg.value) {
          replacements.push({ type: 'text', value: seg.value });
        }
      } else {
        replacements.push({
          type: 'highlight',
          children: [{ type: 'text', value: seg.value }],
        });
      }
    }

    parent.children.splice(index, 1, ...replacements);
  });
}

/**
 * Remark/unified plugin: mutates the mdast between parse and the Milkdown schema parser.
 * Must run in the remark pipeline **before** the document is converted to ProseMirror.
 */
export const remarkHighlightSyntax = $remark('brainfish-highlight-syntax', () => {
  return () => (tree: Root) => {
    transformHighlightSyntax(tree);
  };
});

/**
 * Constructs where mdast attention/escaping rules apply; aligned with
 * `mdast-util-gfm-strikethrough` so `=` inside phrasing can start `==` without bogus escaping.
 */
const constructsWithoutHighlight = [
  'autolink',
  'destinationLiteral',
  'destinationRaw',
  'reference',
  'titleQuote',
  'titleApostrophe',
] as const;

/**
 * Passed into `remarkStringifyOptionsCtx.unsafe` in `configure.ts`.
 * Declares that `=` may appear in phrasing in ways that are “unsafe” by default but are intentional
 * for our `==highlight==` delimiter pair.
 */
export const highlightMdastUnsafe = [
  {
    character: '=',
    inConstruct: 'phrasing' as const,
    notInConstruct: [...constructsWithoutHighlight],
  },
];

/**
 * Serializes mdast `highlight` nodes to markdown `==...==`.
 *
 * Mirrors the structure of GFM strikethrough’s `delete` handler: tracker + `containerPhrasing` keep
 * line wrapping and nesting correct. `state.enter('highlight', …)` is cast because mdast’s
 * `ConstructName` union does not include our custom node name.
 */
export function highlightMarkdownHandler(node: Parents, _: unknown, state: State, info: Info): string {
  const tracker = state.createTracker(info);
  const exit = state.enter('highlight' as Parameters<State['enter']>[0]);
  let value = tracker.move('==');
  value += state.containerPhrasing(node, {
    ...tracker.current(),
    before: value,
    after: '=',
  });
  value += tracker.move('==');
  exit();

  return value;
}

/**
 * All Milkdown plugins for this feature, in registration order:
 * 1. Remark transform (syntax in mdast)
 * 2. Mark schema
 * 3. Input rule
 * 4. Toggle command
 */
export const textHighlightPlugin: MilkdownPlugin[] = [
  remarkHighlightSyntax,
  highlightSchema,
  highlightInputRule,
  toggleHighlightCommand,
].flat();
